import axios from 'axios';
import { zonedTimeToUtc } from 'date-fns-tz';
const debounce = require('lodash/debounce');

const state = {
  enqueuedCards: {
    cards: [],
    keywords: [],
    relatedCards: [],
  },
  enqueuedCollections: [],
  enqueuedMemberships: [],
  enqueuedCollectionNotes: [],
  enqueuedAnnotations: [],
  enqueuedUserUpdates: [],
  syncing: false, // Used for the getter 'syncStatus' so that the getter responds to 1) changes in enqueued... state and 2) to actions that set syncing to true
  syncingAll: false, // Used to determine whether to set syncing to false again after succesfull persist (if we're syncing all, we only set false if all is done)
  syncError: {
    error: null,
    message: null,
  },
};

const getters = {
  syncStatus: (state, getters) => {
    const pendingItems = [];
    if (getters.pendingCardsNo) pendingItems.push(`${getters.pendingCardsNo} card(s)`);
    if (getters.pendingCollectionsNo) pendingItems.push(`${getters.pendingCollectionsNo} collection(s)`);
    if (getters.pendingMembershipsNo) pendingItems.push(`${getters.pendingMembershipsNo} membership(s)`);
    if (getters.pendingCollectionNotesNo) pendingItems.push(`${getters.pendingCollectionNotesNo} collection note(s)`);
    if (getters.pendingAnnotationsNo) pendingItems.push(`${getters.pendingAnnotationsNo} annotation(s)`);
    if (state.enqueuedUserUpdates.length) pendingItems.push('user update(s)');

    let status = null;
    if (state.syncing) status = 'Syncing';
    else if (pendingItems.length) status = 'Pending';
    else status = 'Synced';

    return {
      status,
      pendingItems,
    };
  },
  pendingCardsNo: state => (state.enqueuedCards.cards.length + state.enqueuedCards.keywords.length + state.enqueuedCards.relatedCards.length),
  pendingCollectionsNo: state => state.enqueuedCollections.length,
  pendingMembershipsNo: state => state.enqueuedMemberships.length,
  pendingCollectionNotesNo: state => state.enqueuedCollectionNotes.length,
  pendingAnnotationsNo: state => state.enqueuedAnnotations.length,
  userId: (state, getters, rootState, rootGetters) => rootGetters['users/userId'] || 1, // temp
};

const actions = {
  enqueueCard({ state, commit, dispatch }, { cards, keywords, relatedCards }) {
    // cards = { action, id, data }; if action = delete, data is unnecessary
    // keywords = { action, id, keyword }; all are necessary
    // relatedCards - { action, ids, note }; note is not necessary
    if (!cards && !keywords && !relatedCards) return;

    const dateLastModified = zonedTimeToUtc(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString();
    let cardId;
    if (cards) cardId = cards.id;
    else if (keywords) cardId = keywords.id;
    else if (relatedCards) [cardId] = relatedCards.ids;
    let cardIndex = state.enqueuedCards.cards.findIndex(card => card.id === cardId);
    if (cardIndex === -1) cardIndex = null;
    let cardBIndex = null; // Used if we have relatedCards

    if (cards) {
      // A delete action can overwrite an enqueued update action
      if (cardIndex || cardIndex === 0) commit('EnqueueForCards', { index: cardIndex, payload: Object.assign({}, state.enqueuedCards.cards[cardIndex], cards) });
      else commit('EnqueueForCards', { index: null, payload: cards });
    }

    else if (keywords) {
      let index = null;
      // A delete can overwrite an add
      if (keywords.action === 'delete') index = state.enqueuedCards.keywords.findIndex(kwEntry => (kwEntry.id === cardId && kwEntry.keyword === keywords.keyword));
      if (index === -1) index = null;

      commit('EnqueueForCardKeywords', { index, payload: keywords });
    }

    else if (relatedCards) {
      const enqueuedRCs = state.enqueuedCards.relatedCards;

      let index = -1;
      // Update should match update entry, add and delete are always a new entry (could be optimized a little more though, with deletes cancelling non-persisted adds / updates)
      if (relatedCards.action === 'update') index = enqueuedRCs.findIndex(entry => (entry.action === 'update' && entry.ids.every(id => relatedCards.ids.includes(id))));
      if (index === -1) index = null;
      commit('EnqueueForRelatedCards', { index, payload: relatedCards });

      cardBIndex = state.enqueuedCards.cards.findIndex(card => card.id === relatedCards.ids[1]);
      if (cardBIndex === -1) cardBIndex = null;
    }

    // dateLastModified -- but not if we're processing a card delete
    if (cards && cards.action === 'delete') {
      dispatch('persistAllDebounced');
      return;
    }
    cardIndex = state.enqueuedCards.cards.findIndex(card => card.id === cardId);
    if (cardIndex === -1) cardIndex = null;
    commit('EnqueueForCards', { index: cardIndex, payload: { action: 'update', id: cardId, data: { dateLastModified } } });
    if (relatedCards) commit('EnqueueForCards', { index: cardBIndex, payload: { action: 'update', id: relatedCards.ids[1], data: { dateLastModified } } });
    dispatch('persistAllDebounced');
  },
  enqueueCollection({ state, commit, dispatch }, payload) {
    // Payload = { action, id, name, descendantCollections }; name for action = update, descendantCollections for action = delete
    if (!payload) return;

    let index = state.enqueuedCollections.findIndex(collection => collection.id === payload.id);
    if (index === -1) index = null;

    commit('EnqueueCollection', { index, payload });
    dispatch('persistAllDebounced');
  },
  enqueueMembership({ state, commit, dispatch }, payload) {
    // Payload = { action, collectionId, newParentId, descendantCollections }; newParentId for action = move, descendantCollections for action = (un)archive
    if (!payload) return;

    let index = state.enqueuedMemberships.findIndex(membership => membership.id === payload.id);
    if (index === -1) index = null;

    commit('EnqueueMembership', { index, payload });
    dispatch('persistAllDebounced');
  },
  enqueueCollectionNote({ state, commit, dispatch }, payload) {
    // payload = { action, collectionId, noteId, title, notes }; action = delete only requires collectionId and noteId
    if (!payload) return;

    let index = state.enqueuedCollectionNotes.findIndex(collectionNote => collectionNote.noteId === payload.noteId);
    if (index === -1) index = null;
    commit('EnqueueCollectionNote', { index, payload });
    dispatch('persistAllDebounced');
  },
  enqueueAnnotation({ state, commit, dispatch }, payload) {
    // payload = { annotationId, cardId, relatedCollectionId, xfdfString };
    if (!payload) return;

    let index = state.enqueuedAnnotations.findIndex(annotation => annotation.annotationId === payload.annotationId);
    if (index === -1) index = null;

    commit('EnqueueAnnotation', { index, payload });
    dispatch('persistAllDebounced');
  },
  enqueueUserUpdate({ commit, dispatch }, payload) {
    if (!payload) return;

    commit('EnqueueUserUpdate', payload);
    dispatch('persistAllDebounced');
  },
  persistCards({ state, commit, getters }, cardId) {
    return new Promise(async (resolve, reject) => {
      // If there are no pending card updates or there is an update that still needs to receive a definite id, do not persist
      if (!getters.pendingCardsNo || state.enqueuedCards.cards.find(item => item.id === 0)) {
        resolve();
        return;
      }
      state.syncing = true;

      let cardsToPersist = {};
      if (cardId) {
        const cards = state.enqueuedCards.cards.filter(entry => entry.id === cardId);
        const keywords = state.enqueuedCards.keywords.filter(entry => entry.id === cardId);
        const relatedCards = state.enqueuedCards.relatedCards.filter(entry => entry.ids.includes(cardId));
        if (cards) cardsToPersist.cards = cards;
        if (keywords) cardsToPersist.keywords = keywords;
        if (relatedCards) cardsToPersist.relatedCards = relatedCards;
      }
      else cardsToPersist = state.enqueuedCards;

      try {
        await axios.put(`cards/${getters.userId}`, cardsToPersist);

        if (cardId) commit('DequeueCards', { cardId });
        else commit('DequeueCards', {});

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistCollections({ state, commit, getters }, collectionId) {
    return new Promise(async (resolve, reject) => {
      if (!getters.pendingCollectionsNo) {
        resolve();
        return;
      }
      state.syncing = true;

      let collectionsToPersist = [];
      if (collectionId) collectionsToPersist.push(state.enqueuedCollections.find(collection => collection.id === collectionId));
      else collectionsToPersist = state.enqueuedCollections;

      try {
        await axios.put(`collections/${getters.userId}`, collectionsToPersist);

        if (collectionId) commit('DequeueCollections', collectionId);
        else commit('DequeueCollections');

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistMemberships({ state, commit, getters }, collectionId) {
    return new Promise(async (resolve, reject) => {
      if (!getters.pendingMembershipsNo) {
        resolve();
        return;
      }
      state.syncing = true;

      let membershipsToPersist = [];
      if (collectionId) membershipsToPersist.push(state.enqueuedMemberships.find(membership => membership.collectionId === collectionId));
      else membershipsToPersist = state.enqueuedMemberships;

      try {
        await axios.put(`collections/memberships/${getters.userId}`, membershipsToPersist);

        if (collectionId) commit('DequeueMemberships', collectionId);
        else commit('DequeueMemberships');

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistCollectionNotes({ state, commit, getters }, noteId) {
    return new Promise(async (resolve, reject) => {
      if (!getters.pendingCollectionNotesNo) {
        resolve();
        return;
      }
      state.syncing = true;

      let collectionNotesToPersist = [];
      if (noteId) collectionNotesToPersist.push(state.enqueuedCollectionNotes.find(note => note.noteId === noteId));
      else collectionNotesToPersist = state.enqueuedCollectionNotes;

      try {
        await axios.put(`collectionnotes/${getters.userId}`, collectionNotesToPersist);

        if (noteId) commit('DequeueCollectionNotes', noteId);
        else commit('DequeueCollectionNotes');

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistAnnotations({ state, commit, getters }, annotationId) {
    return new Promise(async (resolve, reject) => {
      if (!getters.pendingAnnotationsNo) {
        resolve();
        return;
      }
      state.syncing = true;

      let annotationsToPersist = [];
      if (annotationId) annotationsToPersist.push(state.enqueuedAnnotations.find(annotation => annotation.annotationId === annotationId));
      else annotationsToPersist = state.enqueuedAnnotations;

      try {
        await axios.put(`annotations/${getters.userId}`, annotationsToPersist);

        if (annotationId) commit('DequeueAnnotations', annotationId);
        else commit('DequeueAnnotations');

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistUserUpdates({ state, commit, getters }) {
    return new Promise(async (resolve, reject) => {
      if (!state.enqueuedUserUpdates.length) {
        resolve();
        return;
      }
      state.syncing = true;

      try {
        const updateObj = Object.assign({}, state.enqueuedUserUpdates[0]);
        if (updateObj.settings) updateObj.settings = JSON.stringify(updateObj.settings);
        await axios.put(`user/${getters.userId}`, updateObj);

        commit('DequeueUserUpdates');

        resolve();
      }
      catch (err) {
        reject(err);
      }
      finally {
        state.syncing = state.syncingAll || false;
      }
    });
  },
  persistAll({ state, dispatch, commit }, { beforeLeaving = false }) {
    return new Promise(async (resolve, reject) => {
      state.syncingAll = true;

      try {
        const persistPromises = [
          dispatch('persistCards'),
          dispatch('persistCollections'),
          dispatch('persistMemberships'),
          dispatch('persistCollectionNotes'),
          dispatch('persistAnnotations'),
          dispatch('persistUserUpdates'),
        ];
        await Promise.all(persistPromises);

        // Any sync error we had can now be removed
        if (state.syncError.error) commit('SetSyncError', { err: null, message: null });

        resolve();
      }
      catch (err) {
        let customMessage = false;
        if (err.message === 'Network Error') customMessage = 'Could not save changes due to a problem with the network. Please check your internet connection.';

        commit('SetSyncError', { err });
        dispatch('notifications/setError', { error: err, customMessage }, { root: true });
        reject();
      }
      finally {
        state.syncingAll = false;
        state.syncing = false;

        // This is for when persistAll is called from beforeunload in case there are pending updates (see App.vue)
        if (beforeLeaving) dispatch('notifications/setNotification', 'Changes are saved!', { root: true });
      }
    });
  },
  persistAllDebounced: debounce(({ dispatch }) => {
    dispatch('persistAll', {});
  }),
};

const mutations = {
  EnqueueForCards(state, { index, payload }) {
    if (index || index === 0) {
      const enqueuedCard = state.enqueuedCards.cards[index];
      enqueuedCard.action = payload.action;
      if (payload.data) Object.assign(enqueuedCard.data, payload.data);
    }
    else state.enqueuedCards.cards.push(payload);
  },
  EnqueueForCardKeywords(state, { index, payload }) {
    if (index || index === 0) state.enqueuedCards.keywords[index] = payload;
    else state.enqueuedCards.keywords.push(payload);
  },
  EnqueueForRelatedCards(state, { index, payload }) {
    if (index || index === 0) state.enqueuedCards.relatedCards[index] = Object.assign({}, state.enqueuedCards.relatedCards[index], payload);
    else state.enqueuedCards.relatedCards.push(payload);
  },
  DequeueCards(state, { cardId, action }) {
    if (action === 'delete') {
      // Once we have a new cardId for a new card that is not saved yet, it will be enqueued for deletion and dequeued only when the user saves the card. Dequeueing via here.
      const index = state.enqueuedCards.cards.findIndex(entry => (entry.id === cardId && entry.action === 'delete'));
      if (index !== -1) state.enqueuedCards.cards.splice(index, 1);
    }
    else if (cardId) {
      const cardsIndex = state.enqueuedCards.cards.findIndex(entry => entry.id === cardId);
      const keywords = state.enqueuedCards.keywords.filter(entry => entry.id === cardId);
      const relatedCards = state.enqueuedCards.relatedCards.filter(entry => entry.ids.includes(cardId));

      if (cardsIndex !== -1) state.enqueuedCards.cards.splice(cardsIndex, 1);
      if (keywords.length) {
        keywords.forEach(({ id, keyword }) => {
          const keywordsIndex = state.enqueuedCards.keywords.findIndex(entry => entry.id === id && entry.keyword === keyword);
          state.enqueuedCards.keywords.splice(keywordsIndex, 1);
        });
      }
      if (relatedCards.length) {
        relatedCards.forEach(({ ids }) => {
          const relatedCardsIndex = state.enqueuedCards.relatedCards.findIndex(entry => entry.ids.every(id => ids.includes(id)));
          state.enqueuedCards.relatedCards.splice(relatedCardsIndex, 1);
        });
      }
    }
    else {
      state.enqueuedCards = { cards: [], keywords: [], relatedCards: [] };
    }
  },
  EnqueueCollection(state, { index, payload }) {
    if (index || index === 0) {
      const enqueuedCollection = state.enqueuedCollections[index];
      enqueuedCollection.action = payload.action;
      if (payload.name) enqueuedCollection.name = payload.name;
      if (payload.descendantCollections) enqueuedCollection.descendantCollections = payload.descendantCollections;
    }
    else state.enqueuedCollections.push(payload);
  },
  DequeueCollections(state, id) {
    if (id) {
      const index = state.enqueuedCollections.findIndex(collection => collection.id === id);
      if (index !== -1) state.enqueuedCollections.splice(index, 1);
    }
    else state.enqueuedCollections = [];
  },
  EnqueueCollectionNote(state, { index, payload }) {
    if (index || index === 0) {
      // Having an index means note is already enqueued. In that case, update with new values (do not replace)
      const enqueuedCollectionNote = state.enqueuedCollectionNotes[index];
      enqueuedCollectionNote.action = payload.action;
      if (payload.title || payload.title === 0) enqueuedCollectionNote.title = payload.title;
      if (payload.notes || payload.notes === 0) enqueuedCollectionNote.notes = payload.notes;
      if (payload.stars || payload.stars === 0) enqueuedCollectionNote.stars = payload.stars;
      if (payload.newCollectionId) enqueuedCollectionNote.newCollectionId = payload.newCollectionId;
    }
    else state.enqueuedCollectionNotes.push(payload);
  },
  DequeueCollectionNotes(state, noteId) {
    if (noteId) {
      const index = state.enqueuedCollectionNotes.findIndex(note => note.noteId === noteId);
      state.enqueuedCollectionNotes.splice(index, 1);
    }
    else state.enqueuedCollectionNotes = [];
  },
  EnqueueAnnotation(state, { index, payload }) {
    if (index || index === 0) state.enqueuedAnnotations.splice(index, 1, payload);
    else state.enqueuedAnnotations.push(payload);
  },
  DequeueAnnotations(state, annotationId) {
    if (annotationId) {
      const index = state.enqueuedAnnotations.findIndex(annotation => annotation.annotationId === annotationId);
      state.enqueuedAnnotations.splice(index, 1);
    }
    else state.enqueuedAnnotations = [];
  },
  EnqueueMembership(state, { index, payload }) {
    if (index || index === 0) {
      const enqueuedMembership = state.enqueuedMemberships[index];
      if (payload.action === 'move' && enqueuedMembership.action === 'move') state.enqueuedMemberships.splice(index, 1, payload);
      else if ((payload.action === 'archive' && enqueuedMembership.action === 'unarchive') || (payload.action === 'unarchive' && enqueuedMembership.action === 'archive')) {
        // Archive cancels out unarchive or vice versa
        state.enqueuedMemberships.splice(index, 1);
      }
      else state.enqueuedMemberships.push(payload);
    }
    else state.enqueuedMemberships.push(payload);
  },
  DequeueMemberships(state, collectionId) {
    if (collectionId) {
      const index = state.enqueuedMemberships.findIndex(membership => membership.relatedCollectionId === collectionId);
      state.enqueuedMemberships.splice(index, 1);
    }
    else state.enqueuedMemberships = [];
  },
  EnqueueUserUpdate(state, payload) {
    state.enqueuedUserUpdates = [payload];
  },
  DequeueUserUpdates(state) {
    state.enqueuedUserUpdates = [];
  },
  SetSyncError(state, { err, message }) {
    state.syncError.error = err;
    state.syncError.message = message;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
